import { message } from 'antd';
import React, { useState } from 'react';
import * as S from './styles';

// Sub-components
import BatchDetailsHeader from './components/BatchDetailsHeader';
import DraftStatus from './components/DraftStatus';
import ApprovalStatus from './components/ApprovalStatus';
import JobDetailsTab from './components/JobDetailsTab';
import ProofsTab from './components/ProofsTab';
import StatusTab from './components/StatusTab';
import { useSubscription } from '@apollo/client';
import { FETCH_BATCHES } from '../../gql/FetchBatches';
import { abortBatch, approveBatch, batchSchedule, handleApproval, rescheduleBatch } from '../../../../utils/services/statusMonitoringAPI';
import { JOB_ABORT_MESSAGES, JOB_APPROVAL_MESSAGES, JOB_RESCHEDULE_MESSAGES, JOB_DETAILS_UI } from './TranslationValues';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionContext } from '../../../../context/sessionContext';
import { getJobStatus } from '../../../../utils/constants/statusMonitoring';

const NAMESPACES = ['JOB_ABORT_MESSAGES', 'JOB_APPROVAL_MESSAGES', 'JOB_RESCHEDULE_MESSAGES', 'JOB_DETAILS_UI'];

/**
 * BatchDetailsPanel refactored to use smaller components
 */
const BatchDetailsPanel = ({
    jobId,
    batchType,
    users,
    subscribed,
    filters,
    cardCount,
}) => {
      const { data, loading: dataLoading, error } = useSubscription(FETCH_BATCHES, {
        variables: {
          filter: { page: 1, startDate: filters?.filter?.startDate, endDate: filters?.filter?.endDate, jobId, 
            batchType, limit: cardCount,
        },
        },
        skip: false,
        shouldResubscribe: true,
      });
      console.log('Batch details data:', data);
        const batch = data?.batchDocumentStats?.data ? data.batchDocumentStats?.data[0] : undefined;

      const { t } = useTranslation(NAMESPACES);

    const [showReschedule, setShowReschedule] = useState(false);
    const [newDate, setNewDate] = useState(batch?.scheduledDate || '');
    const [newTime, setNewTime] = useState(batch?.scheduledTime || '');
    const [activeTab, setActiveTab] = useState('details');

    const sessionSelector = useCallback(store => store.userData, []);
    const [userDetails] = useSessionContext(sessionSelector);

    const handleCancel = () => {
        if (window.confirm(t(JOB_DETAILS_UI.CANCEL_CONFIRMATION.key, JOB_DETAILS_UI.CANCEL_CONFIRMATION.value, { name: batch?.batchName || batch?.name }))) {
            // onCancelJob?.(batch.id);
            message.success(t(JOB_DETAILS_UI.CANCEL_SUCCESS.key, JOB_DETAILS_UI.CANCEL_SUCCESS.value));
        }
    };

    const handleApprove = useCallback(
        async () => {
            const payload = {
                batchId: batch?.id,
            };

            const isHybrid = batch?.batchType === 'HYBRID';

            try {
                let response;
                if(isHybrid) {
                    response = await approveBatch(payload);
                } else {
                    payload.approvalType = 'approve'; 
                    response = await handleApproval(payload);
                }

                // SUCCESS
                if (response?.statusCode === 7009 || response?.statusCode === 0) {
                    message.success(
                        t(
                            JOB_APPROVAL_MESSAGES.APPROVE_SUCCESS.key,
                            JOB_APPROVAL_MESSAGES.APPROVE_SUCCESS.value,
                        ),
                    );
                    return;
                    // onApproveJob?.(batch.id);
                }

                // FAILURE WITH ERROR CODE
                const errorCode = response?.errors?.[0]?.code;

                let errorMessageConfig;

                switch (errorCode) {
                    case 7005:
                        errorMessageConfig = JOB_APPROVAL_MESSAGES.BATCH_NOT_FOUND;
                        break;
                    case 7008:
                        errorMessageConfig = JOB_APPROVAL_MESSAGES.INVALID_STATE;
                        break;
                    case 7009:
                        errorMessageConfig = JOB_APPROVAL_MESSAGES.NOT_AUTHORIZED;
                        break;
                    default:
                        errorMessageConfig = JOB_APPROVAL_MESSAGES.APPROVE_FAILED;
                }

                message.error(
                    t(errorMessageConfig.key, errorMessageConfig.value),
                );

            } catch (error) {
                console.error('Error approving batch:', error);

                message.error(
                    t(
                        JOB_APPROVAL_MESSAGES.APPROVE_ERROR.key,
                        JOB_APPROVAL_MESSAGES.APPROVE_ERROR.value,
                    ),
                );
            } finally {
                console.log('Approval process function over for batch:', batch?.id);
            }
        },
        [batch?.id, t],
    );



    const handleAbort = useCallback(
        async () => {
            const payload = {
                batchId: batch?.id,
            };

            const isHybrid = batch?.batchType === 'HYBRID';

            try {
                let response;
                if(isHybrid) {
                    response = await abortBatch(payload);
                } else {
                    payload.approvalType = 'reject'; 
                    payload.rejectReason = '';
                    payload.email = [];
                    response = await handleApproval(payload);
                }

                // SUCCESS
                if (response?.statusCode === 7011 || response?.statusCode === 4004) {
                    message.success(
                        t(
                            JOB_ABORT_MESSAGES.ABORT_SUCCESS.key,
                            JOB_ABORT_MESSAGES.ABORT_SUCCESS.value,
                        ),
                    );
                    return;
                }

                // REJECTED
                if(response?.statusCode === 4004) {
                    message.success(t(JOB_DETAILS_UI.REJECT_SUCCESS.key, JOB_DETAILS_UI.REJECT_SUCCESS.value));
                }

                // FAILURE WITH ERROR CODE
                const errorCode = response?.errors?.[0]?.code;

                let errorMessageConfig;

                switch (errorCode) {
                    case 7005:
                        errorMessageConfig = JOB_ABORT_MESSAGES.BATCH_NOT_FOUND;
                        break;
                    case 7011:
                        errorMessageConfig = JOB_ABORT_MESSAGES.ALREADY_STARTED_OR_COMPLETED;
                        break;
                    default:
                        errorMessageConfig = JOB_ABORT_MESSAGES.ABORT_FAILED;
                }

                message.error(
                    t(errorMessageConfig.key, errorMessageConfig.value),
                );

            } catch (error) {
                console.error('Error aborting batch:', error);

                message.error(
                    t(
                        JOB_ABORT_MESSAGES.ABORT_ERROR.key,
                        JOB_ABORT_MESSAGES.ABORT_ERROR.value,
                    ),
                );

            } finally {
                console.log('Abort process function over for batch:', batch?.id);

            }
        },
        [batch?.id, t],
    );


    const handleReschedule = useCallback(
        async () => {
            if (!newDate || !newTime) {
                message.error(
                    t(
                        JOB_RESCHEDULE_MESSAGES.MISSING_DATE_TIME.key,
                        JOB_RESCHEDULE_MESSAGES.MISSING_DATE_TIME.value,
                    ),
                );
            }

            try {

                // Combine date + time → ISO UTC
                const localDateTime = new Date(`${newDate}T${newTime}`);
                const newScheduledTime = localDateTime.toISOString();

                const payload = {
                    batchId: batch?.id,
                    newScheduledTime,
                };

                const isHybrid = batch?.batchType === 'HYBRID';
                let response;

                if(isHybrid) {
                    response = await rescheduleBatch(payload);
                } else {
                    delete payload.newScheduledTime;
                    payload.scheduledTime = newScheduledTime;
                    response = await batchSchedule(payload);
                }

                // SUCCESS
                if (response?.statusCode === 7006 || response?.statusCode === 7006) {
                    message.success(
                        t(
                            JOB_RESCHEDULE_MESSAGES.RESCHEDULE_SUCCESS.key,
                            JOB_RESCHEDULE_MESSAGES.RESCHEDULE_SUCCESS.value,
                        ),
                    );
                    setShowReschedule(false);
                    return;
                }

                // FAILURE WITH ERROR CODE
                const errorCode = response?.errors?.[0]?.code;

                let errorMessageConfig;

                switch (errorCode) {
                    case 7005:
                        errorMessageConfig = JOB_RESCHEDULE_MESSAGES.BATCH_NOT_FOUND;
                        break;
                    case 7006:
                        errorMessageConfig =
                            JOB_RESCHEDULE_MESSAGES.ALREADY_STARTED_OR_COMPLETED;
                        break;
                    default:
                        errorMessageConfig = JOB_RESCHEDULE_MESSAGES.RESCHEDULE_FAILED;
                }

                message.error(
                    t(errorMessageConfig.key, errorMessageConfig.value),
                );

            } catch (error) {
                console.error('Error rescheduling batch:', error);

                message.error(
                    t(
                        JOB_RESCHEDULE_MESSAGES.RESCHEDULE_ERROR.key,
                        JOB_RESCHEDULE_MESSAGES.RESCHEDULE_ERROR.value,
                    ),
                );
            } finally {
                console.log('Reschedule process function over for batch:', batch?.id);
            }
        },
        [batch?.id, t, newDate, newTime],
    );


    const handleDateChange = (date, dateString) => {
        setNewDate(dateString);
    };

    const handleTimeChange = (time, timeString) => {
        setNewTime(timeString);
    };

    const canUserApprove = () => {
        if(batch?.approvalType === 'LEVEL') {
            return getJobStatus(batch?.status, userDetails?.roles);
        } else if (batch?.approvalType === 'USER') {
            return parseInt(batch?.approver) === userDetails?.id;
        }
    }

    const isWaitingForApproval = status => {
      const waitingStatuses = [
        'WAITING_FOR_APPROVAL',
        'waiting_for_approval',
        'waiting_for_approval_level_1',
        'waiting_for_approval_level_2',
        'waiting_for_approval_level_3',
        'waiting_for_approval_level_4',
      ];
      return waitingStatuses.includes(status);
    };

    if(batch === undefined) {
        return <div
          style={{
            display: 'flex',
            height: '100%',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        ><img src={'tech_loader.gif'} style={{ width: '100px' }} alt="" /></div>;   
    }

    // Common container for tabs
    const renderTabs = (isSmallStatus = true, isAdvancedBatch = false) => (
        <S.TabsWrapper>
            <S.TabsHeader>
                <S.TabButton active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                    {t(JOB_DETAILS_UI.JOB_DETAILS_TAB.key, JOB_DETAILS_UI.JOB_DETAILS_TAB.value)}
                    {activeTab === 'details' && <S.TabIndicator />}
                </S.TabButton>
                {isAdvancedBatch && <S.TabButton active={activeTab === 'proofs'} onClick={() => setActiveTab('proofs')}>
                    {t(JOB_DETAILS_UI.PROOFS_PREVIEW_TAB.key, JOB_DETAILS_UI.PROOFS_PREVIEW_TAB.value)}
                    {activeTab === 'proofs' && <S.TabIndicator />}
                </S.TabButton>}
                <S.TabButton active={activeTab === 'status'} onClick={() => setActiveTab('status')}>
                    {t(JOB_DETAILS_UI.JOB_STATUS_TAB.key, JOB_DETAILS_UI.JOB_STATUS_TAB.value)}
                    {activeTab === 'status' && <S.TabIndicator />}
                </S.TabButton>
            </S.TabsHeader>

            <S.TabContent>
                {activeTab === 'details' && <JobDetailsTab batch={batch} />}
                {activeTab === 'proofs' && <ProofsTab batch={batch} />}
                {activeTab === 'status' && <StatusTab jobId={jobId} users={users} subscribed={subscribed} filters={filters} large={!isSmallStatus} />}
            </S.TabContent>
        </S.TabsWrapper>
    );

    // Draft job view
    if (batch?.status === 'draft') {
        return (
            <S.PanelContainer>
                <BatchDetailsHeader batch={batch} handleCancel={handleCancel} />
                <DraftStatus 
                    batch={batch} 
                    // onContinueEdit={onContinueEdit} 
                    handleCancel={handleCancel} 
                />
                <S.TabsWrapper>
                <S.TabContent>
                <JobDetailsTab batch={batch} isDraft={true} />
                </S.TabContent>
                </S.TabsWrapper>
            </S.PanelContainer>
        );
    }

    // Pending approval view
    if (batch?.status !== 'draft') {
        return (
            <S.PanelContainer>
                <BatchDetailsHeader batch={batch} handleCancel={handleCancel} />
                {isWaitingForApproval(batch?.status) && canUserApprove() && (
                <ApprovalStatus 
                    batch={batch}
                    showReschedule={showReschedule}
                    setShowReschedule={setShowReschedule}
                    newDate={newDate}
                    newTime={newTime}
                    handleDateChange={handleDateChange}
                    handleTimeChange={handleTimeChange}
                    handleReschedule={handleReschedule}
                    handleApprove={handleApprove}
                    handleAbort={handleAbort}
                    users={users}
                />)}
                {renderTabs(true, batch?.batchType === 'ADVANCED')}
            </S.PanelContainer>
        );
    }

    // Completed or Running jobs view
    return (
        <S.PanelContainer>
            <BatchDetailsHeader batch={batch} handleCancel={handleCancel} />
            {renderTabs(false)}
        </S.PanelContainer>
    );
};

export default BatchDetailsPanel;
